/**
 * Laatst bekende live-waarde (marges, acties, verkoopsnelheid) in de browser van de bezoeker.
 *
 * **Waarom.** Die waarden komen uit Firestore en zijn pas 1–3 seconden na het laden binnen. Tot
 * dan rekende de site met de kale catalogusprijs: € 0,94 werd € 1,27, en daarna schoven de kaarten
 * nog eens van plek zodra de verkoopsnelheid binnenkwam. Bij élk paginabezoek opnieuw, want de
 * waarde leefde alleen in het geheugen van dat ene tabblad. (Thijs, 11-09-2026: "die prijzen
 * verspringen elke keer".) Het was níét de btw-knop: ook zonder btw-voorkeur sprong het.
 *
 * **Wat dit doet.** De laatst gelezen waarde wordt hier bewaard en bij het volgende bezoek meteen
 * gebruikt. De live read loopt gewoon door; alleen als die iets anders oplevert, wordt er opnieuw
 * getekend. Een gewijzigde marge komt dus nog steeds door, maar een ongewijzigde springt niet.
 *
 * **Waarom niet in de bundel.** De marges per categorie horen niet in een openbaar bestand (zie
 * catalogSnapshot: afgeschermd met een sleutel). De browser van de bezoeker krijgt ze al via
 * Firestore; hier bewaren we alleen wat díé browser zelf al gelezen heeft.
 */
const PREFIX = "glastotaal.start.";
/** Ouder dan dit gebruiken we niet: dan is de kans te groot dat de admin intussen iets wijzigde. */
const MAX_LEEFTIJD_MS = 7 * 24 * 60 * 60 * 1000;

export function leesStartwaarde<T>(naam: string, nu: number = Date.now()): T | null {
  try {
    const raw = localStorage.getItem(PREFIX + naam);
    if (!raw) return null;
    const { t, v } = JSON.parse(raw) as { t?: unknown; v?: T };
    if (typeof t !== "number" || v == null) return null;
    // Klok van de bezoeker verzet (tijdstip in de toekomst) → niet vertrouwen.
    if (nu - t > MAX_LEEFTIJD_MS || t > nu + 60_000) return null;
    return v;
  } catch {
    // Geen storage (privévenster, geblokkeerd) of kapotte JSON: gewoon zonder startwaarde.
    return null;
  }
}

export function bewaarStartwaarde(naam: string, v: unknown, nu: number = Date.now()): void {
  try {
    localStorage.setItem(PREFIX + naam, JSON.stringify({ t: nu, v }));
  } catch {
    /* storage vol of geblokkeerd — dan springt het bij het volgende bezoek, niets ergers */
  }
}

/** Zelfde inhoud? Dan hoeft niemand opnieuw te tekenen. */
export function zelfdeWaarde(a: unknown, b: unknown): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}
